import type { PieceType, PlantFamily, Point } from './types';

export const PIECE_TYPES: readonly PieceType[] = ['I', 'J', 'L', 'O', 'S', 'T', 'Z'];

export const PLANT_FAMILIES: Record<PieceType, PlantFamily> = {
  I: { name: 'Hydrangea blue', color: 0x6688a8, lightColor: 0x9eb8ce, symbol: '✤' },
  J: { name: 'Woodland hosta', color: 0x5f7958, lightColor: 0x9fb394, symbol: '◆' },
  L: { name: 'Terracotta bloom', color: 0xb9674d, lightColor: 0xdf9e86, symbol: '✦' },
  O: { name: 'Golden marigold', color: 0xc7973f, lightColor: 0xe2c477, symbol: '✺' },
  S: { name: 'Sage foliage', color: 0x78927a, lightColor: 0xb4c4ae, symbol: '❧' },
  T: { name: 'Pansy violet', color: 0x746080, lightColor: 0xae9ab7, symbol: '❉' },
  Z: { name: 'Burgundy heuchera', color: 0x744d55, lightColor: 0xac7c83, symbol: '♣' },
};

const BASE_SHAPES: Record<PieceType, readonly Point[]> = {
  I: [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 3, y: 0 },
  ],
  J: [
    { x: 0, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 2, y: 1 },
  ],
  L: [
    { x: 2, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 2, y: 1 },
  ],
  O: [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ],
  S: [
    { x: 1, y: 0 },
    { x: 2, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
  ],
  T: [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 1, y: 1 },
    { x: 2, y: 1 },
  ],
  Z: [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 2, y: 1 },
  ],
};

function normalize(points: readonly Point[]): Point[] {
  const minX = Math.min(...points.map(({ x }) => x));
  const minY = Math.min(...points.map(({ y }) => y));
  return points
    .map(({ x, y }) => ({ x: x - minX, y: y - minY }))
    .sort((a, b) => a.y - b.y || a.x - b.x);
}

function rotateClockwise(points: readonly Point[]): Point[] {
  const height = Math.max(...points.map(({ y }) => y)) + 1;
  return normalize(points.map(({ x, y }) => ({ x: height - 1 - y, y: x })));
}

function createRotations(type: PieceType): readonly (readonly Point[])[] {
  const rotations: Point[][] = [normalize(BASE_SHAPES[type])];
  for (let index = 1; index < 4; index += 1) {
    rotations.push(type === 'O' ? rotations[0]! : rotateClockwise(rotations[index - 1]!));
  }
  return rotations;
}

export const PIECE_ROTATIONS: Record<PieceType, readonly (readonly Point[])[]> = Object.fromEntries(
  PIECE_TYPES.map((type) => [type, createRotations(type)]),
) as Record<PieceType, readonly (readonly Point[])[]>;

export function getCells(type: PieceType, rotation: number): readonly Point[] {
  return PIECE_ROTATIONS[type][rotation % 4]!;
}

export function getPieceWidth(type: PieceType, rotation = 0): number {
  return Math.max(...getCells(type, rotation).map(({ x }) => x)) + 1;
}
